import type { ReactNode } from "react";
import { Icon } from "./Icon.js";

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}

/** Shared empty / loading / error placeholder (uses .empty from the design system). */
export function EmptyState({ icon = <Icon name="empty-box" size={30} />, title, children, action, className }: EmptyStateProps) {
  return (
    <div className={["empty", className].filter(Boolean).join(" ")}>
      <div className="e-ico" aria-hidden="true">
        {icon}
      </div>
      <h3 className="jp">{title}</h3>
      {children ? <p className="jp">{children}</p> : null}
      {action ? <div style={{ marginTop: 16 }}>{action}</div> : null}
    </div>
  );
}

/** Loading spinner state reusing the empty layout. */
export function LoadingState({ label = "読み込み中…" }: { label?: string }) {
  return (
    <div className="empty" role="status" aria-live="polite">
      <div className="e-ico" aria-hidden="true">
        <span
          style={{
            display: "inline-block",
            width: 34,
            height: 34,
            borderRadius: "50%",
            border: "2px solid var(--line)",
            borderTopColor: "var(--hi)",
            animation: "spin 1.1s linear infinite",
          }}
        />
      </div>
      <h3 className="jp">{label}</h3>
    </div>
  );
}

/** Error / offline state with optional retry. */
export function ErrorState({
  message,
  onRetry,
  offline,
}: {
  message?: string;
  onRetry?: () => void;
  offline?: boolean;
}) {
  return (
    <div className="empty" role="alert">
      <div className="e-ico" aria-hidden="true">
        {offline ? <Icon name="offline" size={30} /> : <Icon name="warning" size={30} />}
      </div>
      <h3 className="jp">{offline ? "オフラインです" : "読み込みに失敗しました"}</h3>
      <p className="jp">
        {offline
          ? "ネットワーク接続を確認してください。接続が回復すると自動で再取得します。"
          : message ?? "サーバーに接続できませんでした。時間をおいて再試行してください。"}
      </p>
      {onRetry ? (
        <div style={{ marginTop: 16 }}>
          <button className="btn" onClick={onRetry}>
            再試行
          </button>
        </div>
      ) : null}
    </div>
  );
}
